import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  saveZipGameState,
  updateZipGameTime,
} from "../store/slices/zipGameSlice/zipGameSlice";
import { zipSaveScore } from "../http/http";
import { v4 as uuidv4 } from 'uuid';
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import GameOverAnimation from "../animations/gameOver.json";
import classes from "./ZipGame2.module.css";
import { useTranslation } from "react-i18next";

/**
 * ZipGame
 * -------
 * Drag-line puzzle for Quiz Master.
 *
 * Rules:
 * - Start from checkpoint 1.
 * - Drag through adjacent cells only (up/down/left/right).
 * - Reach checkpoints in serial order: 1 -> 2 -> 3 -> ... -> last.
 * - Every cell must be covered to win.
 * - Wrong paths are allowed. If the player paints a bad route, they must backtrack.
 */

const DIFFICULTIES = {
  easy: {
    label: "Easy",
    rows: 5,
    cols: 5,
    checkpoints: 6,
  },
  medium: {
    label: "Medium",
    rows: 6,
    cols: 6,
    checkpoints: 9,
  },
  hard: {
    label: "Hard",
    rows: 7,
    cols: 7,
    checkpoints: 4,
  },
};

const DIRS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

const CELL_UNIT = 100;
const GAP_UNIT = 8;

function keyOf(r, c) {
  return `${r}-${c}`;
}

function sameCell(a, b) {
  return !!a && !!b && a.r === b.r && a.c === b.c;
}

function isInside(r, c, rows, cols) {
  return r >= 0 && r < rows && c >= 0 && c < cols;
}

function isAdjacent(a, b) {
  return Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1;
}

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function buildNeighbors(rows, cols) {
  const map = new Map();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const neighbors = [];
      for (const [dr, dc] of DIRS) {
        const nr = r + dr;
        const nc = c + dc;
        if (isInside(nr, nc, rows, cols)) neighbors.push({ r: nr, c: nc });
      }
      map.set(keyOf(r, c), neighbors);
    }
  }
  return map;
}

function countTurns(path) {
  let turns = 0;
  for (let i = 1; i < path.length - 1; i++) {
    const a = path[i - 1];
    const b = path[i];
    const c = path[i + 1];
    const d1 = `${b.r - a.r},${b.c - a.c}`;
    const d2 = `${c.r - b.r},${c.c - b.c}`;
    if (d1 !== d2) turns++;
  }
  return turns;
}

function generateHamiltonianPath(rows, cols, maxRestarts = 120) {
  const total = rows * cols;
  const neighbors = buildNeighbors(rows, cols);

  function dfs(current, visited, path) {
    if (path.length === total) return path;

    const currentKey = keyOf(current.r, current.c);
    const nextCandidates = neighbors
      .get(currentKey)
      .filter((n) => !visited.has(keyOf(n.r, n.c)))
      .map((n) => {
        const onward = neighbors
          .get(keyOf(n.r, n.c))
          .filter((m) => !visited.has(keyOf(m.r, m.c))).length;
        return { ...n, onward };
      });

    const ordered = shuffle(nextCandidates).sort((a, b) => a.onward - b.onward);

    for (const next of ordered) {
      const nextKey = keyOf(next.r, next.c);
      visited.add(nextKey);
      path.push({ r: next.r, c: next.c });
      const result = dfs(next, visited, path);
      if (result) return result;
      path.pop();
      visited.delete(nextKey);
    }

    return null;
  }

  for (let attempt = 0; attempt < maxRestarts; attempt++) {
    const start = {
      r: Math.floor(Math.random() * rows),
      c: Math.floor(Math.random() * cols),
    };
    const visited = new Set([keyOf(start.r, start.c)]);
    const path = [{ r: start.r, c: start.c }];
    const result = dfs(start, visited, path);
    if (result) return result;
  }

  const fallback = [];
  for (let r = 0; r < rows; r++) {
    if (r % 2 === 0) {
      for (let c = 0; c < cols; c++) fallback.push({ r, c });
    } else {
      for (let c = cols - 1; c >= 0; c--) fallback.push({ r, c });
    }
  }
  return fallback;
}

function pickCheckpointIndices(totalCells, checkpointCount) {
  if (checkpointCount <= 2) return [0, totalCells - 1];

  const indices = [0];
  const segments = checkpointCount - 1;
  const baseStep = (totalCells - 1) / segments;

  for (let i = 1; i < checkpointCount - 1; i++) {
    const target = Math.round(i * baseStep);
    const wiggle = Math.max(1, Math.floor(baseStep / 3));
    const jitter = Math.floor(Math.random() * (wiggle * 2 + 1)) - wiggle;
    const candidate = Math.max(
      indices[indices.length - 1] + 2,
      Math.min(totalCells - (checkpointCount - i), target + jitter)
    );
    indices.push(candidate);
  }

  indices.push(totalCells - 1);

  const uniqueSorted = [...new Set(indices)].sort((a, b) => a - b);
  uniqueSorted[0] = 0;
  uniqueSorted[uniqueSorted.length - 1] = totalCells - 1;
  return uniqueSorted;
}

function makePuzzle(difficultyKey = "easy") {
  const difficulty = DIFFICULTIES[difficultyKey];
  let bestPath = null;
  let bestScore = -1;

  for (let i = 0; i < 6; i++) {
    const candidate = generateHamiltonianPath(difficulty.rows, difficulty.cols);
    const score = countTurns(candidate);
    if (score > bestScore) {
      bestScore = score;
      bestPath = candidate;
    }
  }

  const checkpointIndices = pickCheckpointIndices(bestPath.length, difficulty.checkpoints);
  const checkpointMap = new Map();
  const checkpointCells = [];

  checkpointIndices.forEach((pathIndex, idx) => {
    const cell = bestPath[pathIndex];
    checkpointMap.set(keyOf(cell.r, cell.c), idx + 1);
    checkpointCells.push(cell);
  });

  return {
    difficultyKey,
    difficultyLabel: difficulty.label,
    rows: difficulty.rows,
    cols: difficulty.cols,
    totalCells: difficulty.rows * difficulty.cols,
    solutionPath: bestPath,
    checkpointIndices,
    checkpointMap,
    checkpointCells,
    checkpointCount: checkpointCells.length,
  };
}

function buildRound(difficultyKey,t) {
  const puzzle = makePuzzle(difficultyKey);
  return {
    puzzle,
    playerPath: [puzzle.checkpointCells[0]],
    message: t("184"),
    won: false,
    stats: {
      moves: 0,
      hintsUsed: 0,
      startTime: Date.now(),
    },
  };
}

function getCheckpointNumberAtCell(cell, puzzle) {
  return puzzle.checkpointMap.get(keyOf(cell.r, cell.c)) || null;
}

function getReachedCheckpointNumber(path, puzzle) {
  let reached = 0;
  let targetIndex = 0;

  for (const cell of path) {
    if (
      targetIndex < puzzle.checkpointCells.length &&
      sameCell(cell, puzzle.checkpointCells[targetIndex])
    ) {
      reached += 1;
      targetIndex += 1;
    }
  }

  return reached;
}

function getNextCheckpointNumber(path, puzzle) {
  const reached = getReachedCheckpointNumber(path, puzzle);
  return reached < puzzle.checkpointCount ? reached + 1 : null;
}

function cellCenter(cell) {
  return {
    x: cell.c * (CELL_UNIT + GAP_UNIT) + CELL_UNIT / 2,
    y: cell.r * (CELL_UNIT + GAP_UNIT) + CELL_UNIT / 2,
  };
}

function formatTime(totalSeconds) {
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function serializePuzzle(puzzle) {
  if (!puzzle) return null;

  return {
    ...puzzle,
    checkpointMap: Object.fromEntries(puzzle.checkpointMap || new Map()),
  };
}

function hydratePuzzle(puzzle) {
  if (!puzzle) return null;

  return {
    ...puzzle,
    checkpointMap: new Map(Object.entries(puzzle.checkpointMap || {})),
  };
}

function serializeRound(round) {
  if (!round) return null;

  return {
    ...round,
    puzzle: serializePuzzle(round.puzzle),
  };
}

function hydrateRound(round) {
  if (!round) return null;

  return {
    ...round,
    puzzle: hydratePuzzle(round.puzzle),
  };
}

export default function ZipGame2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const savedZipGame = useSelector(
    (state) => state?.persisted?.zipGameSlice
  );

  const { userZip } = useSelector((state) => state?.persisted?.userZipGameTokenSlice);

  const hasSavedGame = !!(
    savedZipGame?.hasActiveGame && savedZipGame?.round
  );

  const initialDifficulty = hasSavedGame
    ? savedZipGame.difficultyKey
    : "medium";

  const initialRound = hasSavedGame
    ? hydrateRound(savedZipGame.round)
    : buildRound(initialDifficulty,t);

  const [difficultyKey, setDifficultyKey] = useState(initialDifficulty);
  const [round, setRound] = useState(initialRound);
  const [elapsed, setElapsed] = useState(savedZipGame?.time || 0);
  const [isDrawing, setIsDrawing] = useState(false);

  const boardRef = useRef(null);
  const lastTouchedKeyRef = useRef(null);
  const firstDifficultyRenderRef = useRef(true);

  useEffect(() => {
    if (!location?.state?.play && !hasSavedGame) {
      navigate("/zip-game-details", { replace: true });
    }
  }, [location?.state?.play, hasSavedGame, navigate]);

  const startFreshRound = (nextDifficulty = difficultyKey) => {
    const fresh = buildRound(nextDifficulty,t);
    setRound(fresh);
    setElapsed(0);
    setIsDrawing(false);
    lastTouchedKeyRef.current = null;

    dispatch(
      saveZipGameState({
        gameTrxId: uuidv4(),
        difficultyKey: nextDifficulty,
        round: serializeRound(fresh),
        time: 0,
      })
    );
  };

  useEffect(() => {
    if (firstDifficultyRenderRef.current) {
      firstDifficultyRenderRef.current = false;
      return;
    }

    startFreshRound(difficultyKey);
  }, [difficultyKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        saveZipGameState({
          gameTrxId: savedZipGame?.gameTrxId ?? uuidv4(),
          difficultyKey,
          round: serializeRound(round),
          time: elapsed,
        })
      );
    }, 120);

    return () => clearTimeout(timer);
  }, [dispatch, difficultyKey, round, elapsed]);

  const { puzzle, playerPath, won } = round;

  useEffect(() => {
    let id;
    if (!won) {
      const updateTimer = () => {
        const nextElapsed = Math.floor(
          (Date.now() - round.stats.startTime) / 1000
        );
        setElapsed(nextElapsed);
        dispatch(updateZipGameTime(nextElapsed));
      };

      updateTimer();
      id = setInterval(updateTimer, 1000);
    }
    return () => clearInterval(id);
  }, [round.stats.startTime, dispatch, won]);

  const handleSaveScore = async () => {
    try {
      setLoading(true);
      const response = await zipSaveScore(userZip?.token, elapsed, round?.stats?.moves, savedZipGame?.gameTrxId);
      console.log(response?.data?.result, 'response-');

      navigate("/zip-game-result", {
        state: {
          game_trx_id: savedZipGame?.gameTrxId
        }
      })
    } catch (error) {
      console.log(error, 'error--');
      toast.error(t("86"));
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (won) {
      // save score of the user please
      handleSaveScore();
    }
  }, [won]);

  const visitedSet = useMemo(() => {
    const set = new Set();
    playerPath.forEach((cell) => set.add(keyOf(cell.r, cell.c)));
    return set;
  }, [playerPath]);

  const tail = playerPath[playerPath.length - 1];
  const progress = Math.round((playerPath.length / puzzle.totalCells) * 100);
  const reachedCheckpoint = getReachedCheckpointNumber(playerPath, puzzle);
  const nextCheckpoint = getNextCheckpointNumber(playerPath, puzzle);

  const boardWidth = puzzle.cols * CELL_UNIT + (puzzle.cols - 1) * GAP_UNIT;
  const boardHeight = puzzle.rows * CELL_UNIT + (puzzle.rows - 1) * GAP_UNIT;

  const polylinePoints = useMemo(() => {
    return playerPath
      .map((cell) => {
        const pt = cellCenter(cell);
        return `${pt.x},${pt.y}`;
      })
      .join(" ");
  }, [playerPath]);

  const modifyPath = (targetCell, isStartingGesture = false) => {
    setRound((prev) => {
      if (prev.won) return prev;

      const { puzzle: puzzleData, playerPath: path } = prev;
      const tailCell = path[path.length - 1];
      const previousCell = path[path.length - 2] || null;
      const visitedIndex = path.findIndex((cell) => sameCell(cell, targetCell));

      if (isStartingGesture) {
        if (visitedIndex !== -1) {
          const trimmed = path.slice(0, visitedIndex + 1);
          const next = getNextCheckpointNumber(trimmed, puzzleData);
          const reached = getReachedCheckpointNumber(trimmed, puzzleData);
          return {
            ...prev,
            playerPath: trimmed,
            won: false,
            message: next
              ? `${t("185")} ${reached} ${t("186")} ${next}.`
              : `${t("187")}`,
            stats: {
              ...prev.stats,
              moves: prev.stats.moves + 1,
            },
          };
        }

        if (sameCell(tailCell, targetCell)) {
          const next = getNextCheckpointNumber(path, puzzleData);
          return {
            ...prev,
            message: next
              ? `${t("188")} ${next}.`
              : t("189"),
          };
        }

        return prev;
      }

      if (!isAdjacent(tailCell, targetCell)) return prev;

      if (previousCell && sameCell(previousCell, targetCell)) {
        const nextPath = path.slice(0, -1);
        const next = getNextCheckpointNumber(nextPath, puzzleData);
        const reached = getReachedCheckpointNumber(nextPath, puzzleData);
        return {
          ...prev,
          playerPath: nextPath,
          won: false,
          message: next
            ? `${t("190")} ${reached} ${t("191")} ${next}.`
            : `${t("192")}`,
          stats: {
            ...prev.stats,
            moves: prev.stats.moves + 1,
          },
        };
      }

      if (visitedIndex !== -1) {
        const trimmed = path.slice(0, visitedIndex + 1);
        const next = getNextCheckpointNumber(trimmed, puzzleData);
        const reached = getReachedCheckpointNumber(trimmed, puzzleData);
        return {
          ...prev,
          playerPath: trimmed,
          won: false,
          message: next
            ? `${t("193")} ${reached} ${t("194")} ${next}.`
            : `${t("195")}`,
          stats: {
            ...prev.stats,
            moves: prev.stats.moves + 1,
          },
        };
      }

      const alreadyReached = getReachedCheckpointNumber(path, puzzleData);

      if (alreadyReached === puzzleData.checkpointCount) {
        return {
          ...prev,
          message: t("196"),
        };
      }

      const targetCheckpointNumber = getCheckpointNumberAtCell(targetCell, puzzleData);
      const expectedCheckpointNumber = alreadyReached + 1;

      if (targetCheckpointNumber && targetCheckpointNumber !== expectedCheckpointNumber) {
        return {
          ...prev,
          message: `${t("197")} ${expectedCheckpointNumber} ${t("198")}`,
        };
      }

      const nextPath = [...path, targetCell];
      const reachedAfterMove = getReachedCheckpointNumber(nextPath, puzzleData);
      const nextAfterMove = getNextCheckpointNumber(nextPath, puzzleData);
      const allCovered = nextPath.length === puzzleData.totalCells;
      const endedOnLastCheckpoint = sameCell(
        targetCell,
        puzzleData.checkpointCells[puzzleData.checkpointCells.length - 1]
      );
      const nowWon =
        allCovered &&
        reachedAfterMove === puzzleData.checkpointCount &&
        endedOnLastCheckpoint;

      let message = nextAfterMove
        ? `${t("199")} ${nextAfterMove}.`
        : t("200");

      if (targetCheckpointNumber === expectedCheckpointNumber && nextAfterMove) {
        message = `${t("201")} ${targetCheckpointNumber} ${t("202")} ${nextAfterMove}.`;
      }

      if (!targetCheckpointNumber && nextAfterMove) {
        message = `${t("203")} ${nextAfterMove}.`;
      }

      if (reachedAfterMove === puzzleData.checkpointCount && !allCovered) {
        message = t("204");
      }

      if (
        allCovered &&
        reachedAfterMove === puzzleData.checkpointCount &&
        !endedOnLastCheckpoint
      ) {
        message = t("205");
      }

      if (nowWon) {
        message = `${t("206")} ${puzzleData.checkpointCount} ${t("207")}`;
      }

      return {
        ...prev,
        playerPath: nextPath,
        won: nowWon,
        message,
        stats: {
          ...prev.stats,
          moves: prev.stats.moves + 1,
        },
      };
    });
  };

  const getCellFromPointerEvent = (event) => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const cellEl = element?.closest?.("[data-cell]");
    if (!cellEl) return null;
    const value = cellEl.getAttribute("data-cell");
    if (!value) return null;
    const [r, c] = value.split("-").map(Number);
    return { r, c };
  };

  const onBoardPointerDown = (event) => {
    const target = event.target.closest?.("[data-cell]");
    if (!target) return;
    const [r, c] = target.getAttribute("data-cell").split("-").map(Number);

    boardRef.current?.focus();
    boardRef.current?.setPointerCapture?.(event.pointerId);
    setIsDrawing(true);
    lastTouchedKeyRef.current = keyOf(r, c);
    modifyPath({ r, c }, true);
  };

  const onBoardPointerMove = (event) => {
    if (!isDrawing) return;
    const cell = getCellFromPointerEvent(event);
    if (!cell) return;

    const currentKey = keyOf(cell.r, cell.c);
    if (lastTouchedKeyRef.current === currentKey) return;
    lastTouchedKeyRef.current = currentKey;
    modifyPath(cell, false);
  };

  const onBoardPointerUp = () => {
    setIsDrawing(false);
    lastTouchedKeyRef.current = null;
  };

  const onBoardKeyDown = (event) => {
    if (won) return;

    const tailCell = round.playerPath[round.playerPath.length - 1];
    let next = null;

    if (event.key === "ArrowUp") next = { r: tailCell.r - 1, c: tailCell.c };
    if (event.key === "ArrowDown") next = { r: tailCell.r + 1, c: tailCell.c };
    if (event.key === "ArrowLeft") next = { r: tailCell.r, c: tailCell.c - 1 };
    if (event.key === "ArrowRight") next = { r: tailCell.r, c: tailCell.c + 1 };
    if (!next) return;

    event.preventDefault();
    if (!isInside(next.r, next.c, puzzle.rows, puzzle.cols)) return;
    modifyPath(next, false);
  };

  const resetBoard = () => {
    setRound((prev) => ({
      ...prev,
      playerPath: [prev.puzzle.checkpointCells[0]],
      message: t("208"),
      won: false,
      stats: {
        ...prev.stats,
        // moves: 0,
        hintsUsed: 0,
        // startTime: Date.now(),
      },
    }));
    // setElapsed(0);
    setIsDrawing(false);
    lastTouchedKeyRef.current = null;
    // dispatch(updateZipGameTime(0));
  };

  const giveHint = () => {
    if (won) return;

    setRound((prev) => {
      const { puzzle: puzzleData, playerPath: path } = prev;
      const hiddenNext = puzzleData.solutionPath[path.length];
      if (!hiddenNext) return prev;

      const tailCell = path[path.length - 1];
      const previousCell = path[path.length - 2] || null;

      if (previousCell && sameCell(previousCell, hiddenNext)) {
        return prev;
      }

      if (!isAdjacent(tailCell, hiddenNext)) {
        return {
          ...prev,
          message: t("209"),
        };
      }

      const targetCheckpointNumber = getCheckpointNumberAtCell(hiddenNext, puzzleData);
      const expectedCheckpointNumber =
        getReachedCheckpointNumber(path, puzzleData) + 1;

      if (targetCheckpointNumber && targetCheckpointNumber !== expectedCheckpointNumber) {
        return prev;
      }

      const nextPath = [...path, hiddenNext];
      const reachedAfterMove = getReachedCheckpointNumber(nextPath, puzzleData);
      const nextAfterMove = getNextCheckpointNumber(nextPath, puzzleData);
      const allCovered = nextPath.length === puzzleData.totalCells;
      const endedOnLastCheckpoint = sameCell(
        hiddenNext,
        puzzleData.checkpointCells[puzzleData.checkpointCells.length - 1]
      );
      const nowWon =
        allCovered &&
        reachedAfterMove === puzzleData.checkpointCount &&
        endedOnLastCheckpoint;

      let message = nextAfterMove ? `Hint used. Continue to ${nextAfterMove}.` : "Hint used.";
      if (reachedAfterMove === puzzleData.checkpointCount && !allCovered) {
        message =
          "Hint reached the last checkpoint. You still need to backtrack and cover all blocks.\n";
      }
      if (nowWon) {
        message = `Perfect! You connected 1 to ${puzzleData.checkpointCount} and covered every block.`;
      }

      return {
        ...prev,
        playerPath: nextPath,
        won: nowWon,
        message,
        stats: {
          ...prev.stats,
          hintsUsed: prev.stats.hintsUsed + 1,
        },
      };
    });
  };

  return (
    <div className="zip-shell">
      <style>{`
        * { box-sizing: border-box; }
        .zip-shell {
          min-height: 100vh;
          width: 100%;
          padding: 16px;
          background:
            radial-gradient(circle at top, rgba(255,255,255,0.14), transparent 28%),
            linear-gradient(180deg, #cf003c 0%, #e10057 48%, #ed0b63 100%);
               background: #FF6600;
                background: #F6020E;
          font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          color: #0c1538;
        }
        .zip-app {
          max-width: 1100px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1.2fr) minmax(300px, 0.8fr);
          gap: 18px;
          align-items: start;
        }
        .zip-card {
          background: rgba(255,255,255,0.98);
          border-radius: 24px;
          border: 1px solid rgba(9, 20, 66, 0.08);
          box-shadow: 0 18px 40px rgba(13, 18, 56, 0.18);
        }
        .zip-main {
          padding: 18px;
        }
        .zip-side {
          padding: 18px;
          position: sticky;
          top: 16px;
        }
        .zip-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 14px;
        }
        .zip-brand {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .zip-logo {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0c1b73 0%, #3045d2 100%);
          color: #fff;
          display: grid;
          cursor: pointer;
          place-items: center;
          font-size: 22px;
          font-weight: 900;
          box-shadow: 0 10px 22px rgba(18, 35, 123, 0.24);
        }
        .zip-title {
          margin: 0;
          font-size: 28px;
          font-weight: 900;
          line-height: 1;
          color: #0a1233;
        }
        .zip-subtitle {
          margin: 6px 0 0;
          font-size: 14px;
          color: #606a87;
        }
        .zip-select,
        .zip-btn {
          height: 44px;
          padding: 0 14px;
          border-radius: 14px;
          border: 1px solid #d6def0;
          background: #fff;
          color: #0a1233;
          font-weight: 800;
          font-size: 14px;
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .zip-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 18px rgba(14, 23, 61, 0.08);
        }
        .zip-btn.primary {
          background: #0c1b73;
          color: #fff;
          border-color: #0c1b73;
        }
        .zip-btn.accent {
          background: #ffeff5;
          color: #b3004a;
          border-color: #ffcde0;
        }
        .zip-board-wrap {
          background: linear-gradient(180deg, #f9fbff 0%, #f1f5fc 100%);
          border: 1px solid #dce3f2;
          border-radius: 22px;
          padding: 14px;
        }
        .zip-board-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }
        .zip-board-tip {
          font-size: 13px;
          color: #69738f;
        }
        .zip-board {
          position: relative;
          display: grid;
          gap: 8px;
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
          outline: none;
        }
        .zip-board:focus {
          box-shadow: 0 0 0 3px rgba(47, 70, 210, 0.14);
          border-radius: 18px;
        }
        .zip-line-layer {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
        .zip-cell {
          aspect-ratio: 1 / 1;
          border-radius: 14px;
          border: 2px solid #b9c5d9;
          background: #dfe7f2;
          position: relative;
          min-height: 42px;
          z-index: 0;
          overflow: hidden;
        }
        .zip-cell.visited {
          background: #dff6ee;
          border-color: #92d9c1;
        }
        .zip-cell.tail {
          box-shadow: 0 0 0 3px rgba(30, 184, 134, 0.18);
        }
        .zip-anchor {
          position: absolute;
          inset: 0;
          display: grid;
          place-items: center;
          z-index: 3;
        }
        .zip-anchor-ring {
          width: min(74%, 50px);
          height: min(74%, 50px);
          border-radius: 999px;
          background: rgba(255,255,255,0.7);
          display: grid;
          place-items: center;
          transition: transform 0.2s ease, background 0.2s ease;
        }
        .zip-anchor.reached .zip-anchor-ring {
          background: rgba(220, 255, 244, 0.9);
          transform: scale(1.08);
        }
        .zip-anchor-dot {
          width: min(54%, 34px);
          height: min(54%, 34px);
          border-radius: 999px;
          background: linear-gradient(135deg, #0a1029 0%, #1c2d69 100%);
          color: #fff;
          display: grid;
          place-items: center;
          font-size: clamp(12px, 2vw, 16px);
          font-weight: 900;
          box-shadow: 0 6px 12px rgba(9, 16, 46, 0.22);
        }
        .zip-anchor.current .zip-anchor-ring {
          box-shadow: 0 0 0 8px rgba(20, 199, 138, 0.18);
        }
        .zip-side-title {
          margin: 0 0 12px;
          font-size: 19px;
          font-weight: 900;
          color: #0a1233;
        }
        .zip-stats {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 14px;
        }
        .zip-stat {
          background: #f7f9ff;
          border: 1px solid #dce5f7;
          border-radius: 16px;
          padding: 12px;
        }
        .zip-stat-label {
          font-size: 12px;
          font-weight: 800;
          color: #6d7896;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 4px;
        }
        .zip-stat-value {
          font-size: 24px;
          font-weight: 900;
          color: #0a1233;
        }
        .zip-progress {
          height: 14px;
          border-radius: 999px;
          background: #e6edf9;
          overflow: hidden;
          border: 1px solid #dbe2f3;
          margin-bottom: 14px;
        }
        .zip-progress-fill {
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, #0d1d72 0%, #20b887 50%, #ef0a63 100%);
          transition: width 0.2s ease;
        }
        .zip-message {
          background: #fff3f7;
          border: 1px solid #ffd4e2;
          color: #9c0041;
          border-radius: 16px;
          padding: 12px 14px;
          font-weight: 800;
          line-height: 1.45;
          margin-bottom: 14px;
          white-space: pre-line;
        }
        .zip-actions {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .zip-rules {
          margin: 14px 0 0;
          padding-left: 18px;
          color: #596480;
          font-size: 14px;
          line-height: 1.6;
        }
        .zip-rules li + li {
          margin-top: 4px;
        }
        .zip-win {
          margin-top: 14px;
          padding: 14px;
          border-radius: 18px;
          background: linear-gradient(135deg, #edf6ff 0%, #e8fff7 100%);
          border: 1px solid #d1e3fb;
          color: #091436;
          font-weight: 900;
        }
        @media (max-width: 960px) {
          .zip-app {
            grid-template-columns: 1fr;
          }
          .zip-side {
            position: static;
          }
        }
        @media (max-width: 640px) {
          .zip-shell {
            padding: 10px;
          }
          .zip-main,
          .zip-side {
            padding: 14px;
          }
          .zip-title {
            font-size: 24px;
          }
          .zip-stats,
          .zip-actions {
            grid-template-columns: 1fr 1fr;
          }
          .zip-select,
          .zip-btn {
            height: 42px;
            font-size: 13px;
          }
          .zip-board-wrap {
            padding: 10px;
          }
          .zip-board {
            gap: 6px;
          }
          .zip-cell {
            border-radius: 11px;
            min-height: 36px;
          }
        }
      `}</style>

      <div className="zip-app">
        <section className="zip-card zip-main">
          <div className="zip-head">
            <div className="zip-brand">
              <div className="zip-logo" onClick={() => navigate(-1)}>
                <FaArrowLeft />
              </div>
              <div>
                <h1 className="zip-title">Zip Game</h1>
                <p className="zip-subtitle">
                  {t("210")}
                </p>
              </div>
            </div>
          </div>

          {loading ? <div className={classes.game_over_animation_container}>
            <Lottie animationData={GameOverAnimation} loop autoPlay className={classes.gameOverAnimation} />
          </div> : (

            <div className="zip-board-wrap">
              <div className="zip-board-head">
                <div className="zip-board-tip">
                  {t("211")}
                </div>
              </div>

              <div
                ref={boardRef}
                className="zip-board"
                style={{ gridTemplateColumns: `repeat(${puzzle.cols}, minmax(0, 1fr))` }}
                tabIndex={0}
                onKeyDown={onBoardKeyDown}
                onPointerDown={onBoardPointerDown}
                onPointerMove={onBoardPointerMove}
                onPointerUp={onBoardPointerUp}
                onPointerCancel={onBoardPointerUp}
                onPointerLeave={onBoardPointerUp}
              >
                <svg
                  className="zip-line-layer"
                  viewBox={`0 0 ${boardWidth} ${boardHeight}`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  {playerPath.length > 1 && (
                    <>
                      <polyline
                        points={polylinePoints}
                        fill="none"
                        stroke="#b4ebda"
                        strokeWidth="58"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points={polylinePoints}
                        fill="none"
                        stroke="#1fba89"
                        strokeWidth="42"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </>
                  )}
                </svg>

                {Array.from({ length: puzzle.rows }).map((_, r) =>
                  Array.from({ length: puzzle.cols }).map((__, c) => {
                    const cellKey = keyOf(r, c);
                    const checkpointNumber = puzzle.checkpointMap.get(cellKey) || null;
                    const isVisited = visitedSet.has(cellKey);
                    const isTail = sameCell(tail, { r, c });
                    const isCheckpointReached =
                      checkpointNumber && reachedCheckpoint >= checkpointNumber;
                    const isCurrentTarget =
                      checkpointNumber && nextCheckpoint === checkpointNumber;

                    return (
                      <div
                        key={cellKey}
                        data-cell={cellKey}
                        className={`zip-cell ${isVisited ? "visited" : ""} ${isTail ? "tail" : ""}`}
                      >
                        {checkpointNumber && (
                          <div
                            className={`zip-anchor ${isCheckpointReached ? "reached" : ""} ${isCurrentTarget ? "current" : ""}`}
                          >
                            <div className="zip-anchor-ring">
                              <div className="zip-anchor-dot">{checkpointNumber}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </section>

        <aside className="zip-card zip-side">
          <h2 className="zip-side-title">{t("212")}</h2>

          <div className="zip-stats">
            <div className="zip-stat">
              <div className="zip-stat-label">{t("213")}</div>
              <div className="zip-stat-value">{progress}%</div>
            </div>
            <div className="zip-stat">
              <div className="zip-stat-label">{t("214")}</div>
              <div className="zip-stat-value">{formatTime(elapsed)}</div>
            </div>

            <div className="zip-stat">
              <div className="zip-stat-label">{t("215")}</div>
              <div className="zip-stat-value">{round.stats.moves}</div>
            </div>
          </div>

          <div className="zip-progress">
            <div className="zip-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div className="zip-message">{round.message}</div>

          {!won &&
            <div className="zip-actions">
              <button className="zip-btn" onClick={resetBoard} disabled={won}>
                {t("216")}
              </button>
            </div>
          }

          <ul className="zip-rules">
            <li>{t("217")}</li>
            <li>{t("218")}</li>
            <li>{t("219")}</li>
            <li>{t("220")}</li>
            <li>{t("221")}</li>
          </ul>

          {won && (
            <div className="zip-win">
              {t("222")}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}